import {
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CONSUMES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_HAS_PART,
  RELATION_PART_OF,
  RELATION_PROVIDES_API,
} from '@backstage/catalog-model';
import { EmptyState } from '@backstage/core-components';
import { EntityAdrContent, isAdrAvailable } from '@backstage-community/plugin-adr';
import {
  EntityApiDefinitionCard,
  EntityConsumedApisCard,
  EntityConsumingComponentsCard,
  EntityHasApisCard,
  EntityProvidedApisCard,
  EntityProvidingComponentsCard,
} from '@backstage/plugin-api-docs';
import {
  EntityAboutCard,
  EntityHasComponentsCard,
  EntityHasResourcesCard,
  EntityHasSystemsCard,
  EntityLayout,
  EntityLinksCard,
  EntitySwitch,
  isComponentType,
  isKind,
} from '@backstage/plugin-catalog';
import { Direction, EntityCatalogGraphCard } from '@backstage/plugin-catalog-graph';
import {
  EntityGithubActionsContent,
  EntityRecentGithubActionsRunsCard,
  isGithubActionsAvailable,
} from '@backstage-community/plugin-github-actions';
import { EntityKubernetesContent } from '@backstage/plugin-kubernetes';
import {
  EntityGroupProfileCard,
  EntityMembersListCard,
  EntityOwnershipCard,
  EntityUserProfileCard,
} from '@backstage/plugin-org';
import { EntityTechdocsContent } from '@backstage/plugin-techdocs';
import { ExpandableNavigation, LightBox, ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { Button, Grid, useMediaQuery, useTheme } from '@material-ui/core';
import {
  EntityArgoCDContent,
  EntityArgoCDHistoryCard,
  EntityArgoCDOverviewCard,
  isArgocdAvailable,
} from '@roadiehq/backstage-plugin-argo-cd';
import {
  EntityGithubInsightsLanguagesCard,
  EntityGithubInsightsReadmeCard,
  EntityGithubInsightsReleasesCard,
  isGithubInsightsAvailable,
} from '@roadiehq/backstage-plugin-github-insights';
import {
  AaAboutCard,
  AaApiAboutCard,
  ChangeManagementApplicationInformationCard,
  ChangeManagementLeadershipCard,
  AIOverviewCard,
} from '@runway/plugin-catalog-customizations';

import { EntityDataFlow } from './EntityDataFlow/EntityDataFlow';
import { EntityTeamPullRequestsContent } from '@backstage-community/plugin-github-pull-requests-board';
import { BusinessTransactionStepsCard, ObservabilityInfoCard } from '@runway/plugin-observability';
import { ContainerImagePage } from './ContainerImagePage';
import { EntityLayoutWrapper } from './EntityLayoutWrapper';
import { RoutedGithubEntityContent } from './githubEntityRouting';
import { entityWarningContent } from './entityWarningContent';
import { CicdMenu } from './CICDMenu/CicdMenu';
import { TroubleshooterTabErrorBadge } from './EntityDataFlow/TroubleshooterTabErrorBadge';
import { Mermaid } from 'backstage-plugin-techdocs-addon-mermaid';

const techdocsContent = (
  <EntityTechdocsContent>
    <TechDocsAddons>
      <ExpandableNavigation />
      <ReportIssue />
      <LightBox />
      <Mermaid config={{ theme: 'forest', themeVariables: { lineColor: '#000000' } }} />
    </TechDocsAddons>
  </EntityTechdocsContent>
);

const customEntityFilterKind = [
  'Component',
  'API',
  'System',
  'Template',
  'Observability',
  'Container Image',
  'AIAgent',
];

/**
 * NOTE: This page is designed to work on small screens such as mobile devices.
 * This is based on Material UI Grid. If breakpoints are used, each grid item must set the `xs` prop to a column size or to `true`,
 * since this does not default. If no breakpoints are used, the items will equitably share the asvailable space.
 * https://material-ui.com/components/grid/#basic-grid.
 */

export const cicdContent = (
  <RoutedGithubEntityContent
    isAvailable={isGithubActionsAvailable}
    fallback={
      <EmptyState
        title="No CI/CD available for this entity"
        missing="info"
        description="You need to add an annotation to your component if you want to enable CI/CD for it. You can read more about annotations in Backstage by clicking the button below."
        action={
          <Button
            variant="contained"
            color="primary"
            href="https://backstage.io/docs/features/software-catalog/well-known-annotations"
          >
            Read more
          </Button>
        }
      />
    }
  >
    <EntityGithubActionsContent />
  </RoutedGithubEntityContent>
);

const cicdCard = (
  <RoutedGithubEntityContent isAvailable={isGithubActionsAvailable}>
    <Grid item sm={6}>
      <EntityRecentGithubActionsRunsCard limit={4} />
    </Grid>
  </RoutedGithubEntityContent>
);

export const troubleShooterRoute = (
  <EntityLayout.Route
    path="/troubleshooter"
    title="Troubleshooter"
    tabProps={{
      icon: <TroubleshooterTabErrorBadge />,
    }}
  >
    <EntityDataFlow />
  </EntityLayout.Route>
);

const overviewContent = (
  <Grid container spacing={3} alignItems="stretch">
    {entityWarningContent}
    <Grid item md={6} xs={12}>
      <EntityAboutCard />
    </Grid>

    <Grid item sm={6}>
      <AaAboutCard />
    </Grid>

    <Grid item md={6} xs={12}>
      <EntityCatalogGraphCard height={400} />
    </Grid>

    <Grid item md={6} xs={12}>
      <EntityLinksCard />
    </Grid>

    {cicdCard}

    <EntitySwitch>
      <EntitySwitch.Case if={e => Boolean(isArgocdAvailable(e))}>
        <Grid item sm={6}>
          <EntityArgoCDOverviewCard />
        </Grid>
        <Grid item sm={6}>
          <EntityArgoCDHistoryCard />
        </Grid>
      </EntitySwitch.Case>
      <EntitySwitch.Case>
        <RoutedGithubEntityContent isAvailable={e => Boolean(isGithubInsightsAvailable(e))}>
          <Grid item md={6}>
            <EntityGithubInsightsLanguagesCard />
            <EntityGithubInsightsReleasesCard />
          </Grid>
          <Grid item md={6}>
            <EntityGithubInsightsReadmeCard maxHeight={350} />
          </Grid>
        </RoutedGithubEntityContent>
      </EntitySwitch.Case>
    </EntitySwitch>
  </Grid>
);

export const AiAgentOverviewContent = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Grid container spacing={2} alignItems="flex-start">
      {entityWarningContent}
      <Grid item md={6} xs={12} style={{ order: isSmallScreen ? 2 : 1 }}>
        <Grid container spacing={2} direction="column">
          <Grid item xs={12}>
            <EntityAboutCard />
          </Grid>
          <Grid item xs={12}>
            <EntityCatalogGraphCard height={400} />
          </Grid>
          <RoutedGithubEntityContent isAvailable={isGithubActionsAvailable}>
            <Grid item xs={12}>
              <EntityRecentGithubActionsRunsCard limit={4} />
            </Grid>
          </RoutedGithubEntityContent>
        </Grid>
      </Grid>
      <Grid item md={6} xs={12} style={{ order: isSmallScreen ? 1 : 2 }}>
        <Grid container spacing={2} direction="column">
          <Grid item xs={12}>
            <AIOverviewCard variant="gridItem" />
          </Grid>
          <Grid item xs={12}>
            <AaAboutCard />
          </Grid>
          <Grid item xs={12}>
            <EntityLinksCard />
          </Grid>
          <EntitySwitch>
            <EntitySwitch.Case if={e => Boolean(isArgocdAvailable(e))}>
              <Grid item xs={12}>
                <EntityArgoCDOverviewCard />
              </Grid>
            </EntitySwitch.Case>
          </EntitySwitch>
        </Grid>
      </Grid>
    </Grid>
  );
};

const serviceEntityPage = (
  <EntityLayoutWrapper>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route if={isAdrAvailable} path="/adrs" title="ADRs">
      <EntityAdrContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/ci-cd" title="CI/CD">
      <>
        <CicdMenu children={cicdContent} />
      </>
    </EntityLayout.Route>
    <EntityLayout.Route path="/api" title="API">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item xs={12} md={6}>
          <EntityProvidedApisCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityConsumedApisCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>
    <EntityLayout.Route path="/kubernetes" title="Kubernetes">
      <EntityKubernetesContent />
    </EntityLayout.Route>

    {troubleShooterRoute}

    <EntityLayout.Route path="/argocd" title="Argo CD">
      <EntityArgoCDContent />
    </EntityLayout.Route>
  </EntityLayoutWrapper>
);

const aiAgentEntityPage = (
  <EntityLayoutWrapper>
    <EntityLayout.Route path="/" title="Overview">
      <AiAgentOverviewContent />
    </EntityLayout.Route>

    <EntityLayout.Route if={isAdrAvailable} path="/adrs" title="ADRs">
      <EntityAdrContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/kubernetes" title="Kubernetes">
      <EntityKubernetesContent />
    </EntityLayout.Route>

    {troubleShooterRoute}

    <EntityLayout.Route path="/argocd" title="Argo CD">
      <EntityArgoCDContent />
    </EntityLayout.Route>
  </EntityLayoutWrapper>
);

const websiteEntityPage = (
  <EntityLayoutWrapper>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route if={isAdrAvailable} path="/adrs" title="ADRs">
      <EntityAdrContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/ci-cd" title="CI/CD">
      <>
        <CicdMenu children={cicdContent} />
      </>
    </EntityLayout.Route>
    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>
    <EntityLayout.Route path="/kubernetes" title="Kubernetes">
      <EntityKubernetesContent />
    </EntityLayout.Route>

    {troubleShooterRoute}

    <EntityLayout.Route path="/argocd" title="Argo CD">
      <EntityArgoCDContent />
    </EntityLayout.Route>
  </EntityLayoutWrapper>
);

const defaultEntityPage = (
  <EntityLayoutWrapper>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>
    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>
    <EntityLayout.Route if={isAdrAvailable} path="/adrs" title="ADRs">
      <EntityAdrContent />
    </EntityLayout.Route>
  </EntityLayoutWrapper>
);

const standaloneOverviewContent = (
  <Grid container spacing={3} alignItems="stretch">
    {entityWarningContent}
    <Grid item md={6} xs={12}>
      <EntityAboutCard />
    </Grid>
  </Grid>
);

const standalonePluginPage = (
  <EntityLayoutWrapper>
    <EntityLayout.Route path="/" title="Overview">
      {standaloneOverviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/ci-cd" title="CI/CD">
      <>
        <CicdMenu children={cicdContent} />
      </>
    </EntityLayout.Route>
  </EntityLayoutWrapper>
);

export const componentPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isComponentType('service')}>{serviceEntityPage}</EntitySwitch.Case>

    <EntitySwitch.Case if={isComponentType('website')}>{websiteEntityPage}</EntitySwitch.Case>

    <EntitySwitch.Case if={isComponentType('standalone-plugin')}>{standalonePluginPage}</EntitySwitch.Case>

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);

export const aiAgentPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={entity => entity.spec?.type === 'service'}>{aiAgentEntityPage}</EntitySwitch.Case>

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);

const apiPage = (
  <EntityLayoutWrapper>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item md={6} xs={12}>
          <EntityAboutCard />
        </Grid>

        <Grid item md={6} xs={12}>
          <AaApiAboutCard />
        </Grid>

        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard height={400} />
        </Grid>

        <Grid item sm={6}>
          <EntityLinksCard />
        </Grid>

        <Grid item xs={12}>
          <Grid container>
            <Grid item xs={12} md={6}>
              <EntityProvidingComponentsCard />
            </Grid>
            <Grid item xs={12} md={6}>
              <EntityConsumingComponentsCard />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/definition" title="Definition">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EntityApiDefinitionCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayoutWrapper>
);

const userPage = (
  <EntityLayoutWrapper>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityUserProfileCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard entityFilterKind={customEntityFilterKind} />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayoutWrapper>
);

const groupPage = (
  <EntityLayoutWrapper>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityGroupProfileCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard entityFilterKind={customEntityFilterKind} />
        </Grid>
        <Grid item xs={12}>
          <EntityMembersListCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route path="/pull-requests" title="Pull Requests">
      <EntityTeamPullRequestsContent pullRequestLimit={100} />
    </EntityLayout.Route>
  </EntityLayoutWrapper>
);

const systemPage = (
  <EntityLayoutWrapper>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard height={400} />
        </Grid>
        <Grid item md={6}>
          <EntityHasComponentsCard />
        </Grid>
        <Grid item md={6}>
          <EntityHasApisCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityHasResourcesCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route path="/diagram" title="Diagram">
      <EntityCatalogGraphCard
        direction={Direction.TOP_BOTTOM}
        title="System Diagram"
        height={700}
        relations={[
          RELATION_PART_OF,
          RELATION_HAS_PART,
          RELATION_API_CONSUMED_BY,
          RELATION_API_PROVIDED_BY,
          RELATION_CONSUMES_API,
          RELATION_PROVIDES_API,
          RELATION_DEPENDENCY_OF,
          RELATION_DEPENDS_ON,
        ]}
        unidirectional={false}
      />
    </EntityLayout.Route>
  </EntityLayoutWrapper>
);

const domainPage = (
  <EntityLayoutWrapper>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard height={400} />
        </Grid>
        <Grid item md={6}>
          <EntityHasSystemsCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayoutWrapper>
);

const observabilityPage = (
  <EntityLayoutWrapper>
    <EntityLayout.Route path="/" title="Observability Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6} xs={12}>
          <ObservabilityInfoCard variant="gridItem" />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityAboutCard />
        </Grid>
        <Grid item sm={12}>
          <BusinessTransactionStepsCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard height={300} />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityLinksCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>
  </EntityLayoutWrapper>
);

export const ChangeManagementPage = () => {
  return (
    <EntityLayoutWrapper>
      <EntityLayout.Route path="/" title="Overview">
        <Grid container spacing={3}>
          <Grid item sm={6} xs={12}>
            <ChangeManagementApplicationInformationCard />
          </Grid>
          <Grid item sm={6} xs={12}>
            <ChangeManagementLeadershipCard />
          </Grid>
          <Grid item md={6} xs={12}>
            <EntityCatalogGraphCard height={400} />
          </Grid>
        </Grid>
      </EntityLayout.Route>
    </EntityLayoutWrapper>
  );
};

export const entityPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isKind('ChangeManagementApplication')}>
      <ChangeManagementPage />
    </EntitySwitch.Case>
    <EntitySwitch.Case if={isKind('component')} children={componentPage} />
    <EntitySwitch.Case if={isKind('aiagent')} children={aiAgentPage} />
    <EntitySwitch.Case if={isKind('api')} children={apiPage} />
    <EntitySwitch.Case if={isKind('group')} children={groupPage} />
    <EntitySwitch.Case if={isKind('user')} children={userPage} />
    <EntitySwitch.Case if={isKind('system')} children={systemPage} />
    <EntitySwitch.Case if={isKind('domain')} children={domainPage} />
    <EntitySwitch.Case if={isKind('observability')} children={observabilityPage} />
    <EntitySwitch.Case if={isKind('containerimage')} children={ContainerImagePage} />
    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);
